import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { BUYER_TYPES } from "@/lib/constants";
import { Download, LogOut, RefreshCw, ExternalLink, FileText } from "lucide-react";
import IntelligenceManager from "@/components/admin/IntelligenceManager";

const SOURCES = [
  "Triage Tool", "Checklist Download", "PFAS Risk Watch",
  "Risk Screen Request", "Portfolio Screening", "Contact Form",
];
const TRIAGE_RESULTS = ["Low", "Amber", "Red"];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function loginWithGoogle() {
  const redirectUrl = window.location.origin + "/admin-leads";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function AdminLeads() {
  const location = useLocation();
  const [authState, setAuthState] = useState(
    location.state?.user ? { status: "authed", user: location.state.user } : { status: "checking", user: null }
  );

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return; // AuthCallback will handle
    if (authState.status === "authed") return;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setAuthState({ status: "authed", user: data });
      } catch {
        setAuthState({ status: "anon", user: null });
      }
    })();
  }, [authState.status]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setAuthState({ status: "anon", user: null });
  };

  if (authState.status === "checking") {
    return <FullscreenMsg title="Verifying session…" />;
  }
  if (authState.status === "anon") {
    return <SignInScreen onSignIn={loginWithGoogle} />;
  }
  if (!authState.user?.is_admin) {
    return (
      <FullscreenMsg
        title="Access denied"
        subtitle={`${authState.user?.email} is not authorised for /admin-leads.`}
        action={<Button variant="outline" onClick={logout} data-testid="admin-logout-denied">Sign out</Button>}
      />
    );
  }
  return <AdminBoard user={authState.user} onLogout={logout} />;
}

function AdminBoard({ user, onLogout }) {
  const [filters, setFilters] = useState({
    source: "", buyer_type: "", triage_result: "",
    date_from: "", date_to: "", high_intent: "",
  });
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("leads");

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v == null) return;
      if (k === "high_intent") p.set(k, v === "yes" ? "true" : "false");
      else p.set(k, v);
    });
    return p.toString();
  }, [filters]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/leads?${queryString}`);
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error("Could not load leads.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [queryString]);

  const resetFilters = () => setFilters({
    source: "", buyer_type: "", triage_result: "",
    date_from: "", date_to: "", high_intent: "",
  });

  return (
    <div className="min-h-screen bg-[#0A0B0E] text-white">
      {/* Admin bar */}
      <div className="border-b border-white/[0.08] bg-[#0A0B0E]/95 sticky top-0 z-30 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 border border-white/20 flex items-center justify-center">
              <div className="w-1.5 h-1.5 bg-emerald-500" />
            </div>
            <div className="font-display text-sm tracking-tight">PRI · Admin</div>
            <div className="ml-4 flex items-center gap-1">
              <TabBtn active={tab === "leads"} onClick={() => setTab("leads")} testid="admin-tab-leads">Leads</TabBtn>
              <TabBtn active={tab === "intel"} onClick={() => setTab("intel")} testid="admin-tab-intel">Risk Watch</TabBtn>
            </div>
            {tab === "leads" && <span className="ml-3 kicker hidden md:inline">{count} records</span>}
          </div>
          <div className="flex items-center gap-4">
            <a href={`${API}/samples/risk-screen.pdf`} target="_blank" rel="noopener noreferrer" data-testid="admin-sample-pdf-link"
              className="hidden md:inline-flex items-center gap-2 text-[11px] font-mono-alt tracking-[0.14em] uppercase text-white/60 hover:text-white">
              <FileText className="w-3.5 h-3.5" /> Sample PDF (admin preview)
            </a>
            <div className="text-[11px] font-mono-alt tracking-[0.14em] uppercase text-white/50 hidden md:block">{user?.email}</div>
            <Button variant="outline" size="sm" onClick={onLogout} data-testid="admin-logout-btn" className="border-white/20 text-white hover:bg-white/[0.05]">
              <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </div>

      {tab === "intel" && <IntelligenceManager />}

      {tab === "leads" && (<>
      {/* Filters */}
      <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-6">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <FilterSelect testid="filter-source" label="Source" value={filters.source} onChange={(v)=>setFilters(s=>({...s, source:v}))}
            options={SOURCES} />
          <FilterSelect testid="filter-buyer" label="Buyer type" value={filters.buyer_type} onChange={(v)=>setFilters(s=>({...s, buyer_type:v}))}
            options={BUYER_TYPES} />
          <FilterSelect testid="filter-triage" label="Triage result" value={filters.triage_result} onChange={(v)=>setFilters(s=>({...s, triage_result:v}))}
            options={TRIAGE_RESULTS} />
          <FilterSelect testid="filter-high-intent" label="High-intent" value={filters.high_intent} onChange={(v)=>setFilters(s=>({...s, high_intent:v}))}
            options={["yes","no"]} render={(o)=> o==="yes"?"High-intent only":"Standard only"} />
          <div>
            <div className="text-[10px] font-mono-alt tracking-[0.18em] uppercase text-white/50 mb-1">Date from</div>
            <Input type="date" data-testid="filter-date-from" value={filters.date_from} onChange={(e)=>setFilters(s=>({...s, date_from:e.target.value}))}
              className="bg-[#12141A] border-white/10 rounded-none h-10" />
          </div>
          <div>
            <div className="text-[10px] font-mono-alt tracking-[0.18em] uppercase text-white/50 mb-1">Date to</div>
            <Input type="date" data-testid="filter-date-to" value={filters.date_to} onChange={(e)=>setFilters(s=>({...s, date_to:e.target.value}))}
              className="bg-[#12141A] border-white/10 rounded-none h-10" />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-3 items-center">
          <Button onClick={load} data-testid="admin-refresh-btn" className="bg-white text-[#0A0B0E] hover:bg-white/90 rounded-none">
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button variant="outline" onClick={resetFilters} data-testid="admin-reset-filters" className="border-white/20 text-white hover:bg-white/[0.05] rounded-none">
            Reset filters
          </Button>
          <a
            href={`${API}/admin/leads/export.csv${queryString ? `?${queryString}` : ""}`}
            target="_blank" rel="noopener noreferrer"
            data-testid="admin-export-csv-btn"
            className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 px-4 h-10 text-[12px] font-mono-alt tracking-[0.14em] uppercase hover:bg-emerald-500/25"
          >
            <Download className="w-4 h-4" /> Export CSV
          </a>
        </div>
      </div>

      {/* Table */}
      <div className="max-w-[1600px] mx-auto px-6 lg:px-8 pb-24">
        <div className="border border-white/10 overflow-auto admin-scroll">
          <table className="min-w-[1400px] w-full text-sm">
            <thead className="bg-[#12141A] sticky top-0">
              <tr className="text-left text-[10px] font-mono-alt tracking-[0.18em] uppercase text-white/50">
                <Th>Date</Th><Th>Source</Th><Th>Intent</Th><Th>Name</Th><Th>Email</Th>
                <Th>Mobile</Th><Th>Company</Th><Th>Buyer type</Th><Th>Postcode / location</Th>
                <Th>Triage</Th><Th>Concern</Th><Th>Message</Th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr><td colSpan={12} className="p-8 text-center text-white/40">No leads match your filters.</td></tr>
              )}
              {items.map((row) => (
                <tr key={row.submission_id} data-testid={`admin-row-${row.submission_id}`}
                    className={`border-t border-white/[0.06] hover:bg-white/[0.02] ${row.high_intent ? "bg-emerald-500/[0.04]" : ""}`}>
                  <Td>{formatDate(row.created_at)}</Td>
                  <Td><SourceBadge source={row.source} /></Td>
                  <Td>{row.high_intent
                    ? <span className="text-[10px] font-mono-alt tracking-[0.14em] uppercase bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 px-2 py-1">HIGH</span>
                    : <span className="text-[10px] font-mono-alt tracking-[0.14em] uppercase text-white/40">—</span>}
                  </Td>
                  <Td>{[row.first_name, row.last_name].filter(Boolean).join(" ")}</Td>
                  <Td className="truncate max-w-[220px]">
                    <a href={`mailto:${row.email}`} className="text-white/85 hover:text-white inline-flex items-center gap-1">
                      {row.email} <ExternalLink className="w-3 h-3 opacity-40" />
                    </a>
                  </Td>
                  <Td>{row.mobile}</Td>
                  <Td className="truncate max-w-[180px]">{row.company}</Td>
                  <Td className="truncate max-w-[180px]">{row.buyer_type}</Td>
                  <Td>{row.site_postcode_or_location || "—"}</Td>
                  <Td>{row.triage_result ? <RagPill r={row.triage_result} /> : <span className="text-white/30">—</span>}</Td>
                  <Td className="truncate max-w-[180px]">{row.current_concern || row.development_type || "—"}</Td>
                  <Td className="truncate max-w-[280px] text-white/60">{row.message || "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>)}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 text-[11px] font-mono-alt tracking-[0.14em] uppercase border transition-colors ${
        active ? "bg-white text-[#0A0B0E] border-white" : "border-white/15 text-white/60 hover:text-white"}`}>
      {children}
    </button>
  );
}

function FilterSelect({ label, value, onChange, options, render, testid }) {
  return (
    <div>
      <div className="text-[10px] font-mono-alt tracking-[0.18em] uppercase text-white/50 mb-1">{label}</div>
      <Select value={value || "__all__"} onValueChange={(v)=>onChange(v === "__all__" ? "" : v)}>
        <SelectTrigger data-testid={testid} className="bg-[#12141A] border-white/10 rounded-none h-10">
          <SelectValue placeholder={`All ${label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent className="bg-[#12141A] border-white/10 text-white">
          <SelectItem value="__all__">All</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{render ? render(o) : o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

const Th = ({ children }) => <th className="p-3 whitespace-nowrap">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`p-3 whitespace-nowrap ${className}`}>{children}</td>;

function SourceBadge({ source }) {
  const map = {
    "Triage Tool":         "border-emerald-500/40 text-emerald-300 bg-emerald-500/[0.06]",
    "Checklist Download":  "border-blue-500/40 text-blue-300 bg-blue-500/[0.06]",
    "PFAS Risk Watch":     "border-purple-500/40 text-purple-300 bg-purple-500/[0.06]",
    "Risk Screen Request": "border-amber-500/40 text-amber-300 bg-amber-500/[0.06]",
    "Portfolio Screening": "border-sky-500/40 text-sky-300 bg-sky-500/[0.06]",
    "Contact Form":        "border-white/30 text-white/70 bg-white/[0.04]",
  };
  return (
    <span className={`inline-block border ${map[source] || "border-white/20 text-white/60"} px-2 py-1 text-[10px] font-mono-alt tracking-[0.14em] uppercase`}>
      {source}
    </span>
  );
}
function RagPill({ r }) {
  const cls = {
    Red:   "border-red-500/40 text-red-300 bg-red-500/[0.08]",
    Amber: "border-amber-500/40 text-amber-300 bg-amber-500/[0.08]",
    Low:   "border-emerald-500/40 text-emerald-300 bg-emerald-500/[0.08]",
  }[r] || "border-white/20 text-white/60";
  return <span className={`inline-block border ${cls} px-2 py-1 text-[10px] font-mono-alt tracking-[0.14em] uppercase`}>{r}</span>;
}
function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function SignInScreen({ onSignIn }) {
  return (
    <div className="min-h-screen bg-[#0A0B0E] text-white flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center border border-white/10 p-10">
        <div className="kicker mb-4">Admin sign-in required</div>
        <h1 className="font-display text-3xl leading-tight">PFAS Risk Intelligence · Leads</h1>
        <p className="mt-4 text-sm text-white/60">Only authorised administrators can view lead data. Sign in with your allow-listed Google account.</p>
        <button onClick={onSignIn} data-testid="admin-signin-btn"
          className="mt-8 inline-flex items-center gap-2 bg-white text-[#0A0B0E] px-6 py-3.5 text-[12px] font-mono-alt tracking-[0.18em] uppercase hover-lift hover:bg-white/90 w-full justify-center">
          Sign in with Google
        </button>
      </div>
    </div>
  );
}

function FullscreenMsg({ title, subtitle, action }) {
  return (
    <div className="min-h-screen bg-[#0A0B0E] text-white flex items-center justify-center text-center px-6">
      <div>
        <div className="kicker mb-3">Admin</div>
        <div className="font-display text-3xl">{title}</div>
        {subtitle && <div className="mt-3 text-white/60 text-sm">{subtitle}</div>}
        {action && <div className="mt-6">{action}</div>}
      </div>
    </div>
  );
}
